// src/context/TeamContext.js
import { createContext, useContext, useState, useEffect, useRef } from "react";
import teamService from 'shared/services/teamService';
import { syncTrodoIdentifyAndTeam } from 'shared/utils/authSessionBootstrap';
import { hasAnyAccessToken } from 'shared/utils/authToken';

const TeamContext = createContext();

const isPublicAuthRoute = () => {
  const path = window.location?.pathname || '';
  return (
    path.startsWith('/login') ||
    path.startsWith('/signup') ||
    path.startsWith('/reset-password')
  );
};

export const TeamProvider = ({ children }) => {
  const [selectedTeam, setSelectedTeam] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  
  // Debounce timeout ref
  const debounceTimeoutRef = useRef(null);

  // Function to fetch complete team data using the centralized service
  const fetchCompleteTeamData = async (teamId) => {
    try {
      return await teamService.fetchTeamData(teamId);
    } catch (error) {
      console.error('Error fetching complete team data:', error);
      return null;
    }
  };

  // Function to fetch subscription data using the centralized service
  const fetchSubscriptionData = async (teamId) => {
    try {
      return await teamService.fetchSubscriptionData(teamId);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      return null;
    }
  };

  // Enhanced function to load team data with subscription information
  const loadTeamDataWithSubscription = async (teamData) => {
    try {
      if (!teamData || (!teamData.id && !teamData._id)) {
        return teamData;
      }
      
      // Fetch subscription data in parallel, but only if not cached
      const [completeTeamData, subscriptionData] = await Promise.all([
        fetchCompleteTeamData(teamData.id || teamData._id),
        fetchSubscriptionData(teamData.id || teamData._id)
      ]);
      
      // Merge the complete data
      const enhancedTeamData = {
        ...teamData,
        ...(completeTeamData || {}),
        // subscriptionData is the billing-status payload (effectivePlan/plan/status/etc).
        // Keep it at `selectedTeam.subscription` so the rest of the app can read it consistently.
        subscription: subscriptionData || teamData.subscription || null
      };
      
      return enhancedTeamData;
    } catch (error) {
      console.error('Error loading team data with subscription:', error);
      return teamData; // Return original data if enhancement fails
    }
  };

  // Debounced version of loadTeamDataWithSubscription
  const debouncedLoadTeamData = (teamData, callback) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(async () => {
      const enhancedTeam = await loadTeamDataWithSubscription(teamData);
      callback(enhancedTeam);
    }, 300); // 300ms debounce
  };

  useEffect(() => {
    const loadTeamData = async () => {
      try {
        const teamData = localStorage.getItem('selectedTeamData');
        if (teamData) {
          const parsedTeam = JSON.parse(teamData);

          // Avoid protected API calls on public auth routes (login/signup/reset).
          // This prevents noisy 401s before the user authenticates.
          if (isPublicAuthRoute()) {
            setSelectedTeam(parsedTeam);
            setIsLoading(false);
            return;
          }

          // Only attempt protected refresh calls when there is a session at
          // all. A token that merely expires soon is not "logged out" — the
          // axios 401 interceptor refreshes and retries any real request that
          // goes out, and refusing to make this one because it has under 15s
          // left is what stops that refresh from ever being triggered. This
          // used to leave selectedTeam permanently on stale cached data (no
          // freshly merged subscription) with isLoading already false, so
          // nothing here ever retried — see hasAnyAccessToken's doc comment.
          if (!hasAnyAccessToken()) {
            setSelectedTeam(parsedTeam);
            setIsLoading(false);
            return;
          }
          
          const needsRefresh = !parsedTeam.subscription ||
            !parsedTeam._lastFetched ||
            (Date.now() - parsedTeam._lastFetched > 15 * 60 * 1000); // 15 minutes
          
          if (needsRefresh) {
            // Use debounced loading to prevent rapid successive calls
            debouncedLoadTeamData(parsedTeam, (enhancedTeam) => {
              // Add timestamp to track when data was last fetched
              enhancedTeam._lastFetched = Date.now();
              setSelectedTeam(enhancedTeam);
              
              // Update localStorage with enhanced data and timestamp
              localStorage.setItem('selectedTeamData', JSON.stringify(enhancedTeam));
              
              // Set loading to false after team data is loaded
              setIsLoading(false);
            });
          } else {
            // Use cached data directly without fetching
            setSelectedTeam(parsedTeam);
            setIsLoading(false);
          }
        } else {
          // If no team data, try to get basic team info
          const teamName = localStorage.getItem('selectedTeam');
          if (teamName) {
            setSelectedTeam({ name: teamName });
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading team data:', error);
        // Fallback to basic team name if available
        const teamName = localStorage.getItem('selectedTeam');
        if (teamName) {
          setSelectedTeam({ name: teamName });
        }
        setIsLoading(false);
      }
    };

    loadTeamData();

    // Listen for changes to selectedTeamData in localStorage (debounced)
    const handleStorageChange = (e) => {
      if (e.key === 'selectedTeamData' || e.key === 'selectedTeam') {
        // Debounce storage changes to prevent rapid reloads
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
          setIsLoading(true);
          loadTeamData();
        }, 500);
      }
    };
    
    // Listen for the custom teamDataUpdated event (primary method)
    const handleTeamDataUpdated = async (e) => {
      if (e.detail && e.detail.teamData) {
        // Use debounced loading to prevent rapid successive calls
        debouncedLoadTeamData(e.detail.teamData, (enhancedTeam) => {
          setSelectedTeam(enhancedTeam);
          
          // Update localStorage with enhanced data
          localStorage.setItem('selectedTeamData', JSON.stringify(enhancedTeam));
          
          setIsLoading(false);
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('teamDataUpdated', handleTeamDataUpdated);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('teamDataUpdated', handleTeamDataUpdated);
      // Clear any pending debounced calls
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Trodo Analytics: identify + team group when session and workspace are available (SPA navigation / refresh)
  useEffect(() => {
    if (isLoading) return;
    if (isPublicAuthRoute()) return;

    let user;
    try {
      const raw = localStorage.getItem('User');
      user = raw ? JSON.parse(raw) : null;
    } catch (_) {
      user = null;
    }

    const teamId = selectedTeam?.id ?? selectedTeam?._id;
    if (!user?.email || !teamId) return;

    void syncTrodoIdentifyAndTeam(user, selectedTeam);
  }, [isLoading, selectedTeam]);

  const updateSelectedTeam = async (team) => {
    if (team) {
      // Use debounced loading to prevent rapid successive calls
      debouncedLoadTeamData(team, (enhancedTeam) => {
        // Add timestamp to track when data was last fetched
        enhancedTeam._lastFetched = Date.now();
        setSelectedTeam(enhancedTeam);
        
        // Update both localStorage keys for compatibility
        localStorage.setItem('selectedTeamData', JSON.stringify(enhancedTeam));
        localStorage.setItem('selectedTeam', enhancedTeam.name || team.name || team);
        
        // Dispatch the custom event to notify other components
        window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
          detail: { teamData: enhancedTeam }
        }));
      });
    } else {
      setSelectedTeam(null);
      localStorage.removeItem('selectedTeamData');
      localStorage.removeItem('selectedTeam');
    }
  };

  return (
    <TeamContext.Provider value={{ selectedTeam, setSelectedTeam: updateSelectedTeam, isLoading }}>
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};